import classnames from 'classnames';
import { forwardRef, type PropsWithChildren, type ReactNode, useRef } from 'react';
import * as PrimitiveAccordion from '@radix-ui/react-accordion';
import Icon from './Icon';

export interface AccordionItem {
  id?: string;
  className?: string;
  header: JSX.Element | ReactNode;
  content: JSX.Element[] | ReactNode[];
}

interface AccordionProps {
  items: AccordionItem[];
  classNames?: string;
  headerClassNames?: string;
  reverseExpand?: boolean;
  type: 'single' | 'multiple';
  defaultValue?: string[];
  separator?: JSX.Element | ReactNode;
  onValueChange?: (value: string) => void;
  forceMountContent?: boolean;
}

interface AccordionHeaderProps extends PrimitiveAccordion.AccordionHeaderProps {
  reverseExpand?: boolean;
  headerId: string;
}

interface AccordionContentProps extends Omit<
  PrimitiveAccordion.AccordionContentProps,
  'forceMount'
> {
  forceMount?: boolean;
}

const AccordionContent = ({ children, forceMount }: PropsWithChildren<AccordionContentProps>) => {
  const forceMountProps = forceMount ? { forceMount: true as const } : {};

  return (
    <PrimitiveAccordion.Content
      {...forceMountProps}
      className={classnames(
        'overflow-hidden data-[state=closed]:animate-accordionSlideUp data-[state=open]:animate-accordionSlideDown',
        {
          'data-[state=closed]:invisible data-[state=closed]:h-0': forceMount
        }
      )}
    >
      {children}
    </PrimitiveAccordion.Content>
  );
};

const AccordionHeader = ({
  children,
  className,
  reverseExpand,
  headerId
}: AccordionHeaderProps) => {
  const headerClassNames = classnames('flex', className);
  const headerRef = useRef<HTMLDivElement>(null);

  return (
    <PrimitiveAccordion.Header className={headerClassNames} ref={headerRef}>
      <div
        id={headerId}
        className="relative inset-0 flex w-full items-center justify-between rounded-t-lg py-6"
        data-component={'Accordion'}
      >
        <PrimitiveAccordion.Trigger
          aria-labelledby={headerId}
          data-expand-click-area
          className={classnames(
            'absolute inset-0 z-0 flex w-full flex-col items-center data-[state=closed]:rounded-b-lg [&[data-state=open]>svg]:rotate-180',
            { 'data-[state=open]:rounded-t-none data-[state=open]:rounded-bl-lg': reverseExpand }
          )}
        >
          <Icon
            name="chevron-down"
            size="large"
            aria-hidden
            className="relative top-1/3 shrink-0 self-end transition-transform"
          />
        </PrimitiveAccordion.Trigger>
        {children}
      </div>
    </PrimitiveAccordion.Header>
  );
};

const Accordion = forwardRef<HTMLDivElement, AccordionProps>(
  (
    {
      items,
      type,
      classNames,
      headerClassNames,
      separator,
      defaultValue,
      reverseExpand = false,
      onValueChange,
      forceMountContent = false
    }: AccordionProps,
    ref
  ) => {
    const rootClassNames = classnames('w-full', classNames);

    const accordionItems = items.map(({ id, header, className, content: children }, index) => {
      const headerId = `accordion-header-${index}`;

      return (
        <div key={index}>
          <PrimitiveAccordion.Item
            id={id}
            value={index.toString()}
            className={classnames('overflow-hidden', className)}
          >
            {reverseExpand ? (
              <>
                <AccordionContent forceMount={forceMountContent}>{children}</AccordionContent>
                <AccordionHeader
                  className={headerClassNames}
                  reverseExpand={reverseExpand}
                  headerId={headerId}
                >
                  {header}
                </AccordionHeader>
              </>
            ) : (
              <>
                <AccordionHeader
                  className={headerClassNames}
                  reverseExpand={reverseExpand}
                  headerId={headerId}
                >
                  {header}
                </AccordionHeader>
                <AccordionContent forceMount={forceMountContent}>{children}</AccordionContent>
              </>
            )}
          </PrimitiveAccordion.Item>
          {separator}
        </div>
      );
    });

    if (type === 'multiple') {
      return (
        <PrimitiveAccordion.Root
          ref={ref}
          type="multiple"
          className={rootClassNames}
          defaultValue={defaultValue}
          data-component="Accordion"
        >
          {accordionItems}
        </PrimitiveAccordion.Root>
      );
    }

    return (
      <PrimitiveAccordion.Root
        type="single"
        collapsible
        className={rootClassNames}
        onValueChange={onValueChange}
        data-component="Accordion"
      >
        {accordionItems}
      </PrimitiveAccordion.Root>
    );
  }
);

export default Accordion;
